import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Wand2 } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, ErrorState, SectionLabel } from '@/acc-ui/primitives/Layout';
import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { Button } from '@/acc-ui/primitives/Button';
import { Input } from '@/acc-ui/primitives/Input';
import { SearchInput } from '@/acc-ui/primitives/SearchInput';
import { HighlightText } from '@/acc-ui/primitives/HighlightText';
import { Textarea } from '@/acc-ui/primitives/Textarea';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { Badge } from '@/acc-ui/primitives/Badge';
import { adminApi, type ConfigEntry } from '@/api/other-apis';
import { usePermission } from '@/auth/use-permission';
import { ADMIN_EDIT_CONFIG } from '@loanaudit/shared';

/**
 * Admin Config Editor.
 *
 * Lists all config keys (threshold values, feature toggles, etc). Admin
 * can create/edit/delete keys. Type-aware edit:
 *   - number → numeric input
 *   - boolean → checkbox
 *   - string → text input
 *   - object/array → JSON textarea
 */

type EditorMode = 'closed' | 'create' | 'edit';

export function AdminConfigPage() {
  const qc = useQueryClient();
  const { has } = usePermission();
  const canEdit = has(ADMIN_EDIT_CONFIG);

  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<EditorMode>('closed');
  const [editing, setEditing] = useState<ConfigEntry | null>(null);
  const [deleting, setDeleting] = useState<ConfigEntry | null>(null);

  const listQuery = useQuery({ queryKey: ['adminConfig'], queryFn: adminApi.listConfig });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = listQuery.data ?? [];
    if (!q) return all;
    return all.filter(
      (e) =>
        e.key.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        JSON.stringify(e.value).toLowerCase().includes(q),
    );
  }, [listQuery.data, search]);

  const deleteMut = useMutation({
    mutationFn: (key: string) => adminApi.deleteConfig(key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminConfig'] });
      setDeleting(null);
    },
  });

  const columns: Column<ConfigEntry>[] = [
    {
      key: 'key',
      header: 'Key',
      cell: (e) => (
        <div className="flex flex-col">
          <code className="font-mono text-caption text-brand-blue">
            <HighlightText text={e.key} query={search} />
          </code>
          {e.description && (
            <span className="text-caption text-text-tertiary line-clamp-2">
              <HighlightText text={e.description} query={search} />
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      cell: (e) => (
        <div className="flex items-start gap-2 max-w-md">
          <Badge tone="muted">{valueType(e.value)}</Badge>
          <span className="text-caption text-text-primary font-mono truncate">
            {shortValue(e.value)}
          </span>
        </div>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      cell: (e) => (
        <span className="text-caption text-text-tertiary tabular-nums">
          {new Date(e.updatedAt).toLocaleString()}
        </span>
      ),
      width: '12rem',
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            width: '8rem',
            cell: (e: ConfigEntry) => (
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setEditing(e);
                    setMode('edit');
                  }}
                  aria-label={`Edit ${e.key}`}
                  leftIcon={<Pencil className="w-3.5 h-3.5" />}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setDeleting(e);
                  }}
                  aria-label={`Delete ${e.key}`}
                  leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                >
                  Delete
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <AppShell crumb="Admin" title="Config">
      <PageHeader
        title="Config"
        subtitle="Runtime thresholds and feature toggles the modules read."
        actions={
          canEdit ? (
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => {
                setEditing(null);
                setMode('create');
              }}
            >
              New config key
            </Button>
          ) : null
        }
      />

      <div className="flex items-center gap-2">
        <div className="flex-1 max-w-md">
          <SearchInput
            placeholder="Search by key, description, or value…"
            value={search}
            onChange={setSearch}
          />
        </div>
        <span className="text-caption text-text-tertiary">
          {listQuery.data?.length ?? 0} keys
        </span>
      </div>

      {listQuery.isError ? (
        <ErrorState
          description="Could not load config."
          action={<Button onClick={() => listQuery.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<ConfigEntry>
          columns={columns}
          rows={filtered}
          rowKey={(e) => e.key}
          page={1}
          pageSize={filtered.length || 25}
          total={filtered.length}
          onPageChange={() => undefined}
          loading={listQuery.isLoading}
          emptyMessage={
            search
              ? `No config keys match “${search.trim()}”.`
              : 'No config keys yet.'
          }
        />
      )}

      {/* Editor dialog */}
      {mode !== 'closed' && (
        <ConfigEditorDialog
          mode={mode}
          existing={editing}
          onClose={() => {
            setMode('closed');
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['adminConfig'] });
            setMode('closed');
            setEditing(null);
          }}
        />
      )}

      {/* Delete confirmation */}
      {deleting && (
        <Dialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title={`Delete ${deleting.key}?`}
          description="Deletion is recorded in the audit log. If any module reads this key and no replacement exists, it will fall back to its hard-coded default."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMut.mutate(deleting.key)}
                loading={deleteMut.isPending}
              >
                Delete
              </Button>
            </>
          }
        >
          <div className="text-label text-text-secondary">
            <SectionLabel>Current value</SectionLabel>
            <pre className="mt-1 p-2 bg-slate-900 text-slate-100 rounded-md text-caption overflow-x-auto">
              {shortValue(deleting.value)}
            </pre>
          </div>
        </Dialog>
      )}
    </AppShell>
  );
}

type ValueKind = 'boolean' | 'number' | 'string' | 'json';

function valueType(v: unknown): ValueKind {
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'string') return 'string';
  return 'json';
}

function shortValue(v: unknown): string {
  if (typeof v === 'boolean') return String(v);
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return JSON.stringify(v);
  return JSON.stringify(v);
}

// --------------------- Editor dialog ---------------------

function ConfigEditorDialog({
  mode,
  existing,
  onClose,
  onSaved,
}: {
  mode: EditorMode;
  existing: ConfigEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = mode === 'edit' && existing;
  const [key, setKey] = useState(existing?.key ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [kind, setKind] = useState<ValueKind>(existing ? valueType(existing.value) : 'number');
  const [numberVal, setNumberVal] = useState<string>(
    existing && typeof existing.value === 'number' ? String(existing.value) : '0',
  );
  const [stringVal, setStringVal] = useState<string>(
    existing && typeof existing.value === 'string' ? existing.value : '',
  );
  const [boolVal, setBoolVal] = useState<boolean>(
    existing && typeof existing.value === 'boolean' ? existing.value : false,
  );
  const [jsonVal, setJsonVal] = useState<string>(
    existing && valueType(existing.value) === 'json'
      ? JSON.stringify(existing.value, null, 2)
      : '{\n  \n}',
  );
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      let value: unknown;
      switch (kind) {
        case 'boolean':
          value = boolVal;
          break;
        case 'number': {
          const n = Number(numberVal);
          if (Number.isNaN(n)) throw new Error('Value is not a valid number');
          value = n;
          break;
        }
        case 'string':
          value = stringVal;
          break;
        case 'json':
          try {
            value = JSON.parse(jsonVal);
          } catch (e) {
            throw new Error(`Invalid JSON: ${(e as Error).message}`);
          }
          break;
      }
      if (!key.trim()) throw new Error('Key is required');
      return adminApi.upsertConfig({
        key: key.trim(),
        valueJson: value,
        description: description.trim(),
      });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e instanceof Error ? e.message : 'Save failed'),
  });

  const formatJson = () => {
    try {
      setJsonVal(JSON.stringify(JSON.parse(jsonVal), null, 2));
      setErr(null);
    } catch (e) {
      setErr(`Invalid JSON: ${(e as Error).message}`);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isEdit ? `Edit ${existing.key}` : 'New config key'}
      description={
        isEdit
          ? 'Changing a config key takes effect within 60 seconds (Redis TTL).'
          : 'Keys are dotted snake_case (e.g. "validation.emi_tolerance_inr"). Choose a descriptive name — modules read by key.'
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              mut.mutate();
            }}
            loading={mut.isPending}
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-1">
        <span className="text-caption font-semibold text-text-secondary">Key</span>
        <Input
          placeholder="validation.emi_tolerance_inr"
          value={key}
          onChange={(e) => setKey(e.target.value.toLowerCase())}
          disabled={Boolean(isEdit)}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-caption font-semibold text-text-secondary">Description</span>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <div className="flex items-center gap-2">
        <SectionLabel>Type</SectionLabel>
        {(['number', 'string', 'boolean', 'json'] as ValueKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={
              'px-2.5 py-1 rounded-md text-caption font-medium border transition-colors ' +
              (kind === k
                ? 'bg-brand-blue/10 border-brand-blue/30 text-brand-blue'
                : 'bg-white border-glass-hairline text-text-secondary hover:bg-slate-900/5')
            }
          >
            {k}
          </button>
        ))}
      </div>

      {kind === 'number' && (
        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">Value</span>
          <Input
            type="number"
            step="any"
            value={numberVal}
            onChange={(e) => setNumberVal(e.target.value)}
          />
        </label>
      )}
      {kind === 'string' && (
        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">Value</span>
          <Input value={stringVal} onChange={(e) => setStringVal(e.target.value)} />
        </label>
      )}
      {kind === 'boolean' && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={boolVal}
            onChange={(e) => setBoolVal(e.target.checked)}
            className="w-4 h-4 accent-brand-blue"
          />
          <span className="text-label text-text-primary">
            {boolVal ? 'true' : 'false'}
          </span>
        </label>
      )}
      {kind === 'json' && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-caption font-semibold text-text-secondary">Value (JSON)</span>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Wand2 className="w-3.5 h-3.5" />}
              onClick={formatJson}
            >
              Format
            </Button>
          </div>
          <Textarea
            rows={10}
            spellCheck={false}
            className="font-mono text-caption leading-relaxed"
            value={jsonVal}
            onChange={(e) => setJsonVal(e.target.value)}
          />
        </div>
      )}

      {err && (
        <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {err}
        </div>
      )}
    </Dialog>
  );
}
